import useMediaQuery from '@mui/material/useMediaQuery';
import React from 'react';
import { useIntl } from '@kyruus/intl';
import Theme from '@kyruus/ui-theme';
import PropTypes from 'prop-types';
import { useProviderInteractions } from './useProviderInteractions';
import { ProviderCardWrapper } from '../styles';
import { messages } from '../messages';

/**
 * Wrapper component for provider tiles that conditionally applies mouse and keyboard event handlers
 * based on the isProviderListMapsEnabled prop.
 *
 * @param {Object} props - Component props
 * @param {Function} props.onMouseEnter - Mouse enter event handler
 * @param {Function} props.onMouseLeave - Mouse leave event handler
 * @param {Function} props.onKeyboardFocus - Keyboard focus event handler
 * @param {Function} props.onKeyboardBlur - Keyboard blur event handler
 * @param {boolean} props.isProviderListMapsEnabled - Flag to enable/disable provider list maps
 * @param {ReactNode} props.children - Child elements to render
 * @param {Object} props.provider - Provider data object
 * @param {string} props.providerDisplayName - Display name of the provider
 *
 * @returns {JSX.Element}
 */
export const ProviderTileWrapper = ({
  onProviderHover,
  onKeyboardFocus,
  onKeyboardBlur,
  isProviderListMapsEnabled,
  children,
  provider,
  providerDisplayName,
  ...props
}) => {
  const intl = useIntl();
  const isScreenMediumOrAbove = useMediaQuery(
    `(min-width:${Theme.screen_medium})`
  );

  const {
    handleProviderKeyboardFocus,
    handleProviderKeyboardBlur,
    handleProviderMouseEnter,
    handleProviderMouseLeave
  } = useProviderInteractions({
    onProviderHover,
    onKeyboardFocus,
    onKeyboardBlur
  });

  return isProviderListMapsEnabled && isScreenMediumOrAbove ? (
    <ProviderCardWrapper
      onMouseEnter={handleProviderMouseEnter(provider)}
      onMouseLeave={handleProviderMouseLeave(provider)}
      onFocus={handleProviderKeyboardFocus(provider)}
      onBlur={handleProviderKeyboardBlur}
      tabIndex="0"
      role="group"
      aria-label={intl.formatMessage(
        messages.providerListCardMapInteractionAriaLabel,
        { providerName: providerDisplayName }
      )}
      {...props}
    >
      {children}
    </ProviderCardWrapper>
  ) : (
    <ProviderCardWrapper {...props}>{children}</ProviderCardWrapper>
  );
};

ProviderTileWrapper.displayName = 'ProviderTileWrapper';

ProviderTileWrapper.propTypes = {
  onProviderHover: PropTypes.func,
  onKeyboardFocus: PropTypes.func,
  onKeyboardBlur: PropTypes.func,
  isProviderListMapsEnabled: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  provider: PropTypes.object.isRequired
};
